import React, { useState } from 'react'
import { message, Table, TableColumnsType } from 'antd'
import {
  Box,
  Button,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import AddIcCallIcon from '@mui/icons-material/AddIcCall'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonIcon from '@mui/icons-material/Person'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import CallIcon from '@mui/icons-material/Call'
import DeleteIcon from '@mui/icons-material/Delete'
import SendIcon from '@mui/icons-material/Send'

import { IFilterMapped } from './LeadListCancelFilter'
import {
  DATE_FORMAT,
  ONLY_DATE_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../../../../../constants/global'
import { axiosInstance } from '../../../../../../../hooks/useAxios'
import CircularLoading from '../../../../../../../components/CircularProgress'
import { Tag } from '../../../../../../../components/Tag'
import ImgTag from '../../../../../../../components/ImgTag'
import PageUrls from '../../../../../../../constants/page-urls'
import { Link, useNavigate } from 'react-router-dom'
import Pagination from '../../../../../../../components/Pagination'
import {
  ILead,
  ILeadAssignTo,
  ILeadCustomer,
} from '../../../../../../../types/lead'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../../constants/errors'

interface ILeadListTableProps {
  selectedData: ILead[]
  setSelectedData: (leads: ILead[]) => void
  isLoading: boolean
  data: ILead[]
  onUpdatePipelineListSuccess: () => void
  filter: IFilterMapped
  onSearch: (filter: IFilterMapped) => void
  handlePageChange: (filter: IFilterMapped) => void
  total: number
  onSendLead: (lead: ILead) => void
  onMoveLead: (lead: ILead) => void
}

const LeadListCancelTable = ({
  selectedData,
  setSelectedData,
  isLoading,
  data,
  onUpdatePipelineListSuccess,
  filter,
  onSearch,
  handlePageChange,
  total,
  onMoveLead,
  onSendLead,
}: ILeadListTableProps) => {
  const navigate = useNavigate()
  const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const onHandleUnCancelLead = async (lead: ILead) => {
    setIsLoadingDelete(true)
    try {
      await axiosInstance.put(`/lead/un-cancel-leads`, {
        objectIds: [lead.objectId!],
      })
      message.success(`ยกเลิกการยกเลิกข้อมูลสำเร็จ`)
      onUpdatePipelineListSuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
    setIsLoadingDelete(false)
  }

  const columns: TableColumnsType<ILead & { key: string; numberRow: number }> =
    [
      {
        title: 'ลำดับ',
        dataIndex: 'numberRow',
        key: 'numberRow',
        align: 'center' as const,
        width: 80,
        render: (numberRow: number) => {
          return (
            <Box>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                textAlign={'center'}
              >
                {numberRow}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'ชื่อ-นามสกุล',
        dataIndex: 'name',
        key: 'name',
        align: 'center' as const,
        render: (name: string, lead: ILead) => {
          return (
            <Box
              sx={{
                minWidth: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                textAlign: 'left',
              }}
            >
              <Box ml={1}>
                <Typography variant="subtitle1" color="text.secondary">
                  {name}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {lead.customer?.name}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  {lead?.distribution?.name}
                </Typography>
              </Box>
            </Box>
          )
        },
      },
      {
        title: 'ที่อยู่',
        dataIndex: 'customer',
        key: 'customer',
        align: 'center' as const,
        render: (customer: ILeadCustomer) => {
          return (
            <Box sx={{ minWidth: 170, textAlign: 'left' }}>
              <Typography variant="subtitle2" color="text.secondary">
                {customer?.address} {customer?.subDistrict} ตำบล/แขวง อำเภอ/เขต{' '}
                {customer?.province} {customer?.zipcode}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'หมายเลขโทรศัพท์',
        dataIndex: 'customer',
        key: 'customer',
        width: 120,
        align: 'center' as const,
        render: (customer: ILeadCustomer) => {
          return (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {customer?.phone}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'ผู้ดูแล',
        dataIndex: 'assignTo',
        key: 'assignTo',
        width: 120,
        align: 'center' as const,
        render: (assignTo: ILeadAssignTo) => {
          return (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {assignTo?.name}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'PIPELINE',
        dataIndex: 'pipelineName',
        key: 'pipelineName',
        width: 120,
        align: 'center' as const,
        render: (pipelineName: string) => {
          return (
            <Box sx={{ textAlign: 'center' }}>
              <Tag color={'warning'}>
                <Typography variant="subtitle2">{pipelineName}</Typography>
              </Tag>
            </Box>
          )
        },
      },
      {
        title: 'สถานะ',
        dataIndex: 'activePipelineItemName',
        key: 'activePipelineItemName',
        width: 120,
        align: 'center' as const,
        render: (activePipelineItemName: string, lead: ILead) => {
          return (
            <Box sx={{ textAlign: 'center' }}>
              <Tag color={'error'}>
                <Typography variant="subtitle2">ยกเลิก</Typography>
              </Tag>
              <Typography fontSize={11} mt={1}>
                โดย : {lead.cancelBy ?? '-'}
              </Typography>
              <Typography fontSize={11} mt={0.5}>
                <span>{dayjs(lead.cancelAt).format(DATE_FORMAT)}</span>
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'วันหมดอายุ',
        dataIndex: 'expiredAt',
        key: 'expiredAt',
        width: 160,
        align: 'center' as const,
        render: (expiredAt: string) => {
          return (
            <Box>
              <Tag
                color={dayjs().isAfter(dayjs(expiredAt)) ? 'error' : 'success'}
              >
                <Typography variant="subtitle2">
                  {dayjs().isAfter(dayjs(expiredAt)) ? 'หมดอายุ' : 'ใช้งานได้'}
                </Typography>
              </Tag>
              <Typography variant="subtitle2" color="text.secondary">
                {dayjs(expiredAt).format(ONLY_DATE_FORMAT)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dayjs(expiredAt).format(ONLY_TIME_FORMAT)}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'สร้างเมื่อ',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 160,
        align: 'center' as const,
        render: (createdAt: string) => {
          return (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dayjs(createdAt).format(ONLY_TIME_FORMAT)}
              </Typography>
            </Box>
          )
        },
      },
      {
        dataIndex: '',
        key: 'action',
        width: 80,
        align: 'center' as const,
        render: (_: undefined, lead: ILead) => {
          return (
            <Box>
              <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
                <MoreHorizIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
              >
                <MenuItem
                  component={Link}
                  to={`${PageUrls.CRM.Lead.Detail}/${lead.objectId}`}
                >
                  <PersonIcon />
                  <ListItemText sx={{ marginLeft: 1 }}>
                    ดูรายละเอียดทะเบียนลูกค้า
                  </ListItemText>
                </MenuItem>
                <MenuItem onClick={() => onHandleUnCancelLead(lead)}>
                  <AutorenewIcon />
                  <ListItemText sx={{ marginLeft: 1 }}>
                    ยกเลิกการยกเลิก
                  </ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          )
        },
      },
    ]

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    const keys = newSelectedRowKeys as string[]
    const leadObjects = data?.reduce<{ [key: string]: ILead }>((acc, cur) => {
      acc[cur?.objectId!] = cur
      return acc
    }, {})
    const leads = keys.map((key: string) => {
      return leadObjects?.[key]
    })
    setSelectedData(leads as ILead[])
  }

  const rowSelection = {
    selectedRowKeys: selectedData.map((lead: ILead) => lead.objectId!),
    onChange: onSelectChange,
  }

  return (
    <>
      <CircularLoading isLoading={isLoading || isLoadingDelete} />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
        mb={2}
      />
      <Table
        size={'middle'}
        rowSelection={rowSelection}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map((lead: ILead, index: number) => {
          return {
            ...lead,
            key: lead.objectId!,
            numberRow:
              index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
          }
        })}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          handlePageChange({ ...filter, page, perPage })
        }
      />
    </>
  )
}

export default LeadListCancelTable
